import { type FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { difference } from 'lodash-es';

import { type RootState } from '@editor2/reducers/root';
import { type Chapter } from '@editor2/types/chapter';
import ChangesDiff from '../common/ChangesDiff';
import PageChangesDiff from './PageChangesDiff';
import { pageGroupHeader, pageHeader } from '../styles';
import {
	getChapterByPageGroupId,
	getChapterTitle,
	getEntityById,
	getInitialIndex,
	getPageGroupTitle,
	getPageTitle,
	getParentEntityByPageId
} from '../helpers';
import ExpandChangesButton from '../common/ExpandChangesButton';
import DeletedItem from '../common/DeletedItem';
import ChildrenChangesWrapper from '../common/ChildrenChangesWrapper';

interface Props {
	courseId: number;
	pageGroupChanges: any;
	index: number;
	initChapter?: Chapter;
}

const PageGroupChangesDiff: FC<Props> = (props) => {
	const { courseId, pageGroupChanges, index, initChapter } = props;

	const { id, pages, ...attributeChanges } = pageGroupChanges;

	const { page_groups, chapters, initialCourse } = useSelector(
		(state: RootState) => state.courses[courseId]
	);

	const [showChildren, setShowChildren] = useState(true);

	const initialPageGroup = getEntityById(initialCourse.page_groups, id);
	const currentPageGroup =
		getEntityById(page_groups, id) || page_groups.entities[attributeChanges.family_id];
	const initialIndex = initChapter?.page_groups
		? getInitialIndex(initChapter?.page_groups, initialPageGroup?.family_id)
		: -1;

	if (Object.keys(pageGroupChanges).length === 1 && index === initialIndex) return <></>;

	const getChangesPageIds = () => {
		return pages.map(
			(entity) => entity.family_id || getEntityById(initialCourse.pages, entity.id).family_id
		);
	};

	const getParentName = () => {
		const chapter =
			getChapterByPageGroupId(initialCourse.chapters, currentPageGroup.family_id) ||
			getChapterByPageGroupId(chapters, currentPageGroup.family_id);
		return getChapterTitle(chapter);
	};

	return (
		<>
			<ChangesDiff
				title={getPageGroupTitle(currentPageGroup)}
				index={index}
				initialIndex={initialIndex}
				initial={initialPageGroup}
				attributeChanges={attributeChanges}
				parentName={getParentName()}
				styles={pageGroupHeader}
			/>

			{pages && !showChildren && <ExpandChangesButton onClick={() => setShowChildren(true)} />}

			{showChildren && (
				<ChildrenChangesWrapper setShowChildren={setShowChildren}>
					{pages &&
						pages.map((pageObj, index) => (
							<PageChangesDiff
								courseId={courseId}
								pageChanges={pageObj}
								index={index}
								initPageGroup={initialPageGroup}
								key={index}
							/>
						))}

					{pages &&
						difference(initialPageGroup?.pages || [], getChangesPageIds()).map((family_id) => {
							const pageGroup = getParentEntityByPageId(page_groups, family_id);
							const chapter = getParentEntityByPageId(chapters, family_id);

							const movedTo =
								(pageGroup && getPageGroupTitle(pageGroup)) ||
								(chapter && getChapterTitle(chapter));

							return (
								<DeletedItem
									key={family_id}
									title={getPageTitle(initialCourse.pages.entities[family_id])}
									movedTo={movedTo}
									styles={pageHeader}
								/>
							);
						})}
				</ChildrenChangesWrapper>
			)}
		</>
	);
};

export default PageGroupChangesDiff;
